import { type AnyAction, combineReducers } from 'redux';

import {
    clearFocusedAsset,
    clearTextAssetTrackedSlide,
    clearTrackedSlide,
    clearTrackedSlideTextAssets,
    endPasteUpload,
    hideAllSceneSections,
    hideFullscreenSelector,
    hideFullscreenSwapSceneSelector,
    hideOverlaySelector,
    setFocusedAssetInfo,
    setFocusedSceneInfo,
    setMediaTransformEditState,
    setOverlayForSceneGroup,
    setSceneSelectorForSlide,
    setSwapSceneDesignForSlide,
    showFullscreenSelector,
    showFullscreenSwapSceneSelector as action_showFullscreenSwapSceneSelector,
    showOverlaySelector,
    startPasteUpload,
    trackChangedSceneDesign,
    trackChangedTextAsset,
    trackChangedTextAssetSlide,
} from 'js/src/data/scenes/scenes-actions';
import type {
    MediaTransformEditState,
    OverlayBounds,
    PasteUploadState,
} from 'js/src/data/scenes/scenes-types';
import type { DeepReadonly } from 'js/src/libs/consts';

const hasKey = (record: Readonly<Record<string, unknown>>, key: string) =>
    Object.prototype.hasOwnProperty.call(record, key);

const withoutKey = <T>(
    record: Readonly<Record<string, T>>,
    key: string,
): Readonly<Record<string, T>> => {
    if (!hasKey(record, key)) {
        return record;
    }
    const { [key]: _removed, ...remaining } = record;
    return remaining;
};

const NO_SCENE_IDS: readonly string[] = [];
const NO_TRACKED_TEXT_ASSETS: Readonly<Record<string, readonly string[]>> = {};
const NO_PASTE_UPLOADS: Readonly<Record<string, DeepReadonly<PasteUploadState>>> = {};

/**
 * Contains all slides that have a scene design applied that is different from
 * the default scene design (i.e. a new design was chosen from the scene selector)
 */
export function sceneDesignChangedSet(
    state: readonly string[] = NO_SCENE_IDS,
    action: AnyAction,
): readonly string[] {
    if (trackChangedSceneDesign.match(action)) {
        const { sceneId } = action.payload;
        return state.includes(sceneId) ? state : [...state, sceneId];
    }
    if (clearTrackedSlide.match(action)) {
        const { sceneId } = action.payload;
        return state.includes(sceneId) ? state.filter((id) => id !== sceneId) : state;
    }
    return state;
}

export function sceneDesignTextAssetChangedSet(
    state: Readonly<Record<string, readonly string[]>> = NO_TRACKED_TEXT_ASSETS,
    action: AnyAction,
): Readonly<Record<string, readonly string[]>> {
    if (trackChangedTextAssetSlide.match(action)) {
        return { ...state, [action.payload.sceneId]: [] };
    }
    if (trackChangedTextAsset.match(action)) {
        const { sceneId, assetId } = action.payload;
        const list = hasKey(state, sceneId) ? state[sceneId] : [];
        return { ...state, [sceneId]: [...list, assetId] };
    }
    if (clearTextAssetTrackedSlide.match(action)) {
        return withoutKey(state, action.payload.sceneId);
    }
    if (clearTrackedSlideTextAssets.match(action)) {
        const { sceneId } = action.payload;
        if (!hasKey(state, sceneId) || state[sceneId].length === 0) {
            return state;
        }
        return { ...state, [sceneId]: [] };
    }
    return state;
}

type FullscreenSceneSelectorState = {
    visible: boolean;
    targetSlideIndex: number | null;
    targetSlideId: string | null;
    targetSlideGroupId: string | null;
    sceneGroupCategory: string | null;
};
const HIDDEN_FULLSCREEN_SCENE_SELECTOR: DeepReadonly<FullscreenSceneSelectorState> = {
    visible: false,
    targetSlideIndex: null,
    targetSlideId: null,
    targetSlideGroupId: null,
    sceneGroupCategory: null,
};

export function showFullscreenSceneSelector(
    state: DeepReadonly<FullscreenSceneSelectorState> = HIDDEN_FULLSCREEN_SCENE_SELECTOR,
    action: AnyAction,
): DeepReadonly<FullscreenSceneSelectorState> {
    if (showFullscreenSelector.match(action)) {
        return {
            visible: true,
            targetSlideIndex: action.payload.targetSlideIndex,
            targetSlideId: action.payload.targetSlideId ?? null,
            targetSlideGroupId: action.payload.targetSlideGroupId ?? null,
            sceneGroupCategory: action.payload.sceneGroupCategory ?? null,
        };
    }
    if (hideFullscreenSelector.match(action) || hideAllSceneSections.match(action)) {
        return state.visible ? { ...state, visible: false } : state;
    }
    return state;
}

export function showFullscreenSwapSceneSceneSelector(state = false, action: AnyAction) {
    if (action_showFullscreenSwapSceneSelector.match(action)) {
        return true;
    }
    if (hideFullscreenSwapSceneSelector.match(action)) {
        return false;
    }
    if (hideAllSceneSections.match(action)) {
        return false;
    }
    return state;
}

export function overlaySelectorShowing(state = false, action: AnyAction) {
    if (showOverlaySelector.match(action)) {
        return true;
    }
    if (hideOverlaySelector.match(action)) {
        return false;
    }
    if (hideAllSceneSections.match(action)) {
        return false;
    }
    return state;
}

/**
 * The start and end times of the overlay selector, in video time.
 */
const NO_OVERLAY_BOUNDS: DeepReadonly<OverlayBounds> = { startTime: null, endTime: null };

const withOverlayBounds = (
    state: DeepReadonly<OverlayBounds>,
    startTime: OverlayBounds['startTime'],
    endTime: OverlayBounds['endTime'],
): DeepReadonly<OverlayBounds> =>
    state.startTime === startTime && state.endTime === endTime
        ? state
        : { ...state, startTime, endTime };

export function overlayBounds(
    state: DeepReadonly<OverlayBounds> = NO_OVERLAY_BOUNDS,
    action: AnyAction,
): DeepReadonly<OverlayBounds> {
    if (showOverlaySelector.match(action)) {
        return withOverlayBounds(state, action.payload.startTime, action.payload.endTime);
    }
    if (hideOverlaySelector.match(action) || hideAllSceneSections.match(action)) {
        return withOverlayBounds(state, null, null);
    }
    return state;
}

export function overlaySceneGroupId(state: string | null = null, action: AnyAction) {
    if (showOverlaySelector.match(action)) {
        return action.payload.sceneGroupId;
    }
    if (hideOverlaySelector.match(action)) {
        return null;
    }
    if (hideAllSceneSections.match(action)) {
        return null;
    }
    return state;
}

/**
 * Determine whether the sidebar will render the scene selector, and for
 * which slide it will show scene designs for.
 */
export function sceneSelectorSlideId(state: string | null = null, action: AnyAction) {
    if (setSceneSelectorForSlide.match(action)) {
        return action.payload.sceneId;
    }
    if (setSwapSceneDesignForSlide.match(action)) {
        return action.payload.sceneId ? null : state;
    }
    if (setOverlayForSceneGroup.match(action)) {
        return action.payload.sceneGroupId ? null : state;
    }
    if (hideAllSceneSections.match(action)) {
        return null;
    }
    return state;
}

/**
 * Determine whether the sidebar will render the swap scene design tab
 * and for which slide it will show scene designs for.
 */
export function swapSceneDesignSlideId(state: string | null = null, action: AnyAction) {
    if (setSwapSceneDesignForSlide.match(action)) {
        return action.payload.sceneId;
    }
    if (setSceneSelectorForSlide.match(action)) {
        return action.payload.sceneId ? null : state;
    }
    if (setOverlayForSceneGroup.match(action)) {
        return action.payload.sceneGroupId ? null : state;
    }
    if (hideFullscreenSwapSceneSelector.match(action)) {
        return null;
    }
    if (hideAllSceneSections.match(action)) {
        return null;
    }
    return state;
}

type CurrentlyEditingAssetData = {
    sceneGroupId: string | null;
    sceneId: string | null;
    subsceneIndex: number | null;
    assetId: string | null;
    assetType: string | null;
    rootAssetGroupId?: string | null;
};

/**
 * Which scene group, scene, subscene, and asset are currently in focus. Assets being in focus means they're selected
 * (and are being edited). Scenes can be in focus without an asset being in focus.
 * Not all assets will have a root asset group.
 */
const NOTHING_FOCUSED: DeepReadonly<CurrentlyEditingAssetData> = {
    sceneGroupId: null,
    sceneId: null,
    subsceneIndex: null,
    assetId: null,
    assetType: null,
    rootAssetGroupId: null,
};

export function focusedSceneInfo(
    state: DeepReadonly<CurrentlyEditingAssetData> = NOTHING_FOCUSED,
    action: AnyAction,
): DeepReadonly<CurrentlyEditingAssetData> {
    if (setFocusedSceneInfo.match(action)) {
        return {
            sceneGroupId: action.payload.sceneGroupId,
            sceneId: action.payload.sceneId,
            subsceneIndex: action.payload.subsceneIndex ?? 0,
            assetId: null,
            assetType: null,
            rootAssetGroupId: null,
        };
    }
    if (setFocusedAssetInfo.match(action)) {
        return {
            sceneGroupId: action.payload.sceneGroupId,
            sceneId: action.payload.sceneId,
            subsceneIndex: action.payload.subsceneIndex,
            assetId: action.payload.assetId,
            assetType: action.payload.assetType,
            rootAssetGroupId: action.payload.rootAssetGroupId ?? null,
        };
    }
    if (clearFocusedAsset.match(action)) {
        // clear the asset info but keep the scene info
        if (state.assetId === null && state.assetType === null && state.rootAssetGroupId === null) {
            return state;
        }
        return { ...state, assetId: null, assetType: null, rootAssetGroupId: null };
    }
    return state;
}

export function pasteUploads(
    state: Readonly<Record<string, DeepReadonly<PasteUploadState>>> = NO_PASTE_UPLOADS,
    action: AnyAction,
): Readonly<Record<string, DeepReadonly<PasteUploadState>>> {
    if (startPasteUpload.match(action)) {
        const pasteUploadState: PasteUploadState = {
            assetId: action.payload.assetId,
            sceneId: action.payload.sceneId,
            uploadSessionId: action.payload.uploadSessionId,
            status: 'uploading',
            thumbnailDataUrl: action.payload.thumbnailDataUrl,
        };
        return { ...state, [action.payload.assetId]: pasteUploadState };
    }
    if (endPasteUpload.match(action)) {
        return withoutKey(state, action.payload.assetId);
    }
    return state;
}

// Which asset (if any) has an in-scene media transform session open. Bound to the focused asset,
// so any focus change or clear ends the session.
export function mediaTransformEdit(
    state: MediaTransformEditState = null,
    action: AnyAction,
): MediaTransformEditState {
    if (setMediaTransformEditState.match(action)) {
        return action.payload;
    }
    if (
        setFocusedSceneInfo.match(action) ||
        setFocusedAssetInfo.match(action) ||
        clearFocusedAsset.match(action)
    ) {
        return null;
    }
    return state;
}

export const scenes = combineReducers({
    sceneDesignChangedSet,
    sceneSelectorSlideId,
    swapSceneDesignSlideId,
    showFullscreenSceneSelector,
    showFullscreenSwapSceneSceneSelector,
    sceneDesignTextAssetChangedSet,
    focusedSceneInfo,
    overlaySelectorShowing,
    overlayBounds,
    overlaySceneGroupId,
    pasteUploads,
    mediaTransformEdit,
});

export type ScenesState = ReturnType<typeof scenes>;
